// The course search, offered before a visitor has to go looking for it.
// Design doc: docs/superpowers/specs/2026-09-24-proactive-course-finder-design.md.
//
// Three ways in, one search:
//   - the bar's "Find your course" (a real link to the doorway's finder, so it
//     works with no script — this only intercepts it),
//   - the "/" key, anywhere a visitor is not typing,
//   - the nudge, layouts/_finder_nudge.
// All three open layouts/_finder_dialog — unless the page carries its own
// finder, in which case they scroll to THAT one and focus it. A visitor should
// meet one search box, never two.
//
// PROACTIVE, NOT INTRUSIVE. The nudge never shows on arrival, never covers
// content and never takes focus. It shows once, when a visitor has lingered
// without meeting a finder:
//   - a page WITH a finder: once it has scrolled up past the top of the
//     window untouched, and the visitor has been here SETTLE_MS;
//   - a page WITHOUT one: after IDLE_MS, or half-way down the page.
// Once a visitor touches any search it never comes back; a dismissal keeps it
// away for QUIET_DAYS; it shows at most once a session. Where storage is
// refused it does not show at all — when in doubt, stay silent.
//
// localStorage holds only this visitor's convenience, per the rule every
// storage read in this app follows: wrapped, and the page is right without it.
//
// Two events reach the funnel, through FinderEventsController, which accepts
// no others: finder_prompted when the nudge shows, finder_opened (with the way
// in) when the search opens. Fire-and-forget; a failure changes nothing here.
//
// NOT IN packs/, for the reason course_finder.js records. Early-returns when
// the dialog is absent, which is every layout but the public one.

const SETTLE_MS = 8000;
const IDLE_MS = 12000;
const SCROLL_SHARE = 0.5;
const QUIET_DAYS = 30;
const STORE_KEY = "pq:finder";
const SESSION_KEY = "pq:finder:shown";

// --- storage ---------------------------------------------------------------

// null means "storage refused": the nudge treats that as a reason to stay
// quiet, and everything else carries on without it.
function readState() {
  try {
    return JSON.parse(window.localStorage.getItem(STORE_KEY) || "{}") || {};
  } catch (_error) {
    return null;
  }
}

function writeState(patch) {
  try {
    const state = readState() || {};
    window.localStorage.setItem(STORE_KEY, JSON.stringify({ ...state, ...patch }));
  } catch (_error) {
    // A convenience lost, nothing more.
  }
}

function shownThisSession() {
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === "1";
  } catch (_error) {
    return true;
  }
}

function markShownThisSession() {
  try {
    window.sessionStorage.setItem(SESSION_KEY, "1");
  } catch (_error) {
    // As above.
  }
}

// --- events ----------------------------------------------------------------

function record(endpoint, name, source) {
  const token = document.querySelector('meta[name="csrf-token"]');
  const body = new URLSearchParams({ name, source });

  try {
    fetch(endpoint, {
      method: "POST",
      body,
      keepalive: true,
      credentials: "same-origin",
      headers: token ? { "X-CSRF-Token": token.content } : {}
    }).catch(() => {});
  } catch (_error) {
    // The funnel misses one row; the visitor notices nothing.
  }
}

// --- setup -----------------------------------------------------------------

function setup() {
  const dialog = document.querySelector("[data-finder-dialog]");
  if (!dialog || typeof dialog.showModal !== "function") return;

  const endpoint = dialog.dataset.endpoint;
  const dialogInput = dialog.querySelector(".course-search__input");
  const nudge = document.querySelector("[data-finder-nudge]");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // The page's own finder, if it has one: any search box outside the dialog.
  const pageSearch = Array.from(document.querySelectorAll("[data-course-search]"))
    .find((node) => !dialog.contains(node));
  const pageFinder = pageSearch ? (pageSearch.closest(".course-finder") || pageSearch) : null;

  let opener = null;

  function markUsed() {
    writeState({ used: true });
    hideNudge();
  }

  function openSearch(source, from) {
    markUsed();
    record(endpoint, "finder_opened", source);

    if (pageSearch) {
      const input = pageSearch.querySelector(".course-search__input");
      pageFinder.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
      if (input) input.focus({ preventScroll: true });
      return;
    }

    if (dialog.open) return;
    opener = from || document.activeElement;
    dialog.showModal();
    if (dialogInput) dialogInput.focus();
  }

  // Focus goes back where it came from — unless that was the nudge, which
  // hid itself when the search opened, and a hidden button cannot take focus.
  // Then it goes to the bar's entry, the way back in, rather than to <body>.
  dialog.addEventListener("close", () => {
    if (opener && typeof opener.focus === "function") opener.focus();
    if (document.activeElement !== opener) {
      document.querySelector('[data-finder-open="navbar"]')?.focus();
    }
    opener = null;
  });

  dialog.querySelectorAll("[data-finder-close]").forEach((button) =>
    button.addEventListener("click", () => dialog.close())
  );

  // A click on the backdrop lands on the <dialog> itself; one inside it lands
  // on a descendant.
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });

  document.querySelectorAll("[data-finder-open]").forEach((trigger) => {
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      openSearch(trigger.dataset.finderOpen || "navbar", trigger);
    });
  });

  // "/" is the web's search key. Never while the visitor is typing, never
  // with a modifier (a browser or screen-reader shortcut), never twice.
  document.addEventListener("keydown", (event) => {
    if (event.key !== "/" || event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.defaultPrevented || dialog.open) return;

    const target = event.target;
    if (target instanceof HTMLElement &&
        (target.isContentEditable || target.closest("input, textarea, select"))) return;

    event.preventDefault();
    openSearch("shortcut", null);
  });

  // Touching ANY finder on the page — the hero's search, its picker — is a
  // visitor who has found the way in. The nudge has nothing left to say.
  document.addEventListener("focusin", (event) => {
    if (event.target instanceof Element &&
        event.target.closest("[data-course-search], .course-picker")) {
      markUsed();
    }
  });

  setupNudge();

  // --- the nudge ---------------------------------------------------------

  function hideNudge() {
    if (nudge) nudge.hidden = true;
  }

  function quiet() {
    const state = readState();
    if (state === null || state.used || shownThisSession()) return true;

    const dismissedAt = Number(state.dismissedAt) || 0;
    return Date.now() - dismissedAt < QUIET_DAYS * 24 * 60 * 60 * 1000;
  }

  function setupNudge() {
    if (!nudge || quiet()) return;

    let shown = false;
    const since = Date.now();

    function show() {
      if (shown || quiet() || dialog.open) return;
      shown = true;
      markShownThisSession();
      nudge.hidden = false;
      record(endpoint, "finder_prompted", "nudge");
    }

    nudge.querySelector("[data-finder-nudge-dismiss]")?.addEventListener("click", () => {
      writeState({ dismissedAt: Date.now() });
      hideNudge();
    });

    // Esc from inside the nudge dismisses it, as it would a dialog — but the
    // nudge never takes focus, so this only answers a visitor who tabbed in.
    nudge.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      writeState({ dismissedAt: Date.now() });
      hideNudge();
    });

    if (pageFinder && "IntersectionObserver" in window) {
      // "Scrolled past", not merely "not on screen": a finder below the fold
      // that the visitor has not reached yet is not one they walked by.
      let passed = false;

      const observer = new IntersectionObserver(([entry]) => {
        passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        if (passed && Date.now() - since >= SETTLE_MS) show();
      });
      observer.observe(pageFinder);

      window.setTimeout(() => {
        if (passed) show();
      }, SETTLE_MS);
      return;
    }

    if (pageFinder) return;

    window.setTimeout(show, IDLE_MS);

    function onScroll() {
      const room = document.documentElement.scrollHeight - window.innerHeight;
      if (room > 0 && window.scrollY / room >= SCROLL_SHARE) {
        window.removeEventListener("scroll", onScroll);
        show();
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
  }
}

// Deferred like every pack module; the guard course_finder.js uses.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setup);
} else {
  setup();
}
